import React, { useEffect, useRef, useState, useMemo } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from "../../../../utils/SamuiUtils";

const useChartData = ({ data, sortBy, topCount }) => {
  return useMemo(() => {
    const sorted = [...data]
      .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0))
      .slice(0, topCount);

    const labels = sorted.map(item =>
      item.Item_Name?.length > 20
        ? item.Item_Name.slice(0, 20) + '...'
        : item.Item_Name || item.Item_Code
    );

    const values = {
      qty: sorted.map(item => item.TotalQty || 0),
      revenue: sorted.map(item => item.Item_Total || 0),
      customers: sorted.map(item => item.total_customer || 0),
      repeatRate: sorted.map(item =>
        item.total_customer
          ? parseFloat(((item.total_customer_repeat || 0) / item.total_customer * 100).toFixed(2))
          : 0
      ),
    };

    const dataset = [];
    const common = { borderWidth: 2, borderRadius: 2, borderSkipped: false };

    switch (sortBy) {
      case 'TotalQty':
        dataset.push({ label: 'จำนวนที่ขาย', data: values.qty, backgroundColor: '#ef6c00', borderColor: '#ef6c00', ...common });
        break;
      case 'total_customer':
        dataset.push({ label: 'จำนวนลูกค้า', data: values.customers, backgroundColor: 'rgba(255,193,7,0.8)', borderColor: 'rgba(255,193,7,1)', ...common });
        break;
      case 'repeat_rate':
        dataset.push({ label: 'อัตราซื้อซ้ำ (%)', data: values.repeatRate, backgroundColor: 'rgba(220,53,69,0.8)', borderColor: 'rgba(220,53,69,1)', ...common });
        break;
      case 'Item_Total':
        dataset.push({ label: 'ยอดขาย (บาท)', data: values.revenue, backgroundColor: 'rgba(40,167,69,0.8)', borderColor: 'rgba(40,167,69,1)', yAxisID: 'y', ...common });
        break;
      default:
        dataset.push({ label: 'จำนวนที่ขาย', data: values.qty, backgroundColor: '#0d6efd', borderColor: '#0d6efd', ...common });
    }

    return { labels, dataset, raw: sorted };
  }, [data, sortBy, topCount]);
};

const useChartConfig = ({ labels, dataset, raw, sortBy, onProductClick }) => {
  return useMemo(() => ({
    type: 'bar',
    data: { labels, datasets: dataset },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: `สินค้าขายดี Top ${labels.length}`, font: { size: 18, weight: 'bold' } },
        legend: { position: 'top', labels: { usePointStyle: true } },
        tooltip: {
          callbacks: {
            title: ctx => raw[ctx[0].dataIndex].Item_Name || raw[ctx[0].dataIndex].Item_Code,
            label: ctx => {
              const item = raw[ctx.dataIndex];
              return [`${ctx.dataset.label}: ${ctx.formattedValue}`, `รหัส: ${item.Item_Code}`, `ขาย: ${item.TotalQty} ชิ้น`, `ยอด: ${formatCurrency(item.Item_Total)}`, `ลูกค้า: ${item.total_customer}`];
            },
          },
        },
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 0 } },
        y: {
          ticks: { callback: val => { if (sortBy === 'Item_Total') return formatCurrency(val); return sortBy === 'repeat_rate' ? val + '%' : val.toLocaleString(); } },
          title: { display: true, text: { TotalQty: 'จำนวน (ชิ้น)', total_customer: 'ลูกค้า (ราย)', repeat_rate: 'ซื้อซ้ำ (%)', Item_Total: 'ยอดขาย (บาท)' }[sortBy] },
        },
      },
      onClick: (evt, elems) => {
        if (!elems.length) return;
        const idx = elems[0].index;
        onProductClick && onProductClick(raw[idx].Item_Code);
      },
    },
  }), [labels, dataset, raw, sortBy, onProductClick]);
};

const TopSellingChart = ({ data, onProductClick }) => {
  const [chartType, setChartType] = useState('bar');
  const [sortBy, setSortBy] = useState('TotalQty');
  const [topCount, setTopCount] = useState(10);
  const chartRef = useRef(null);

  const { labels, dataset, raw } = useChartData({ data, sortBy, topCount });
  const config = useChartConfig({ labels, dataset, raw, sortBy, onProductClick });

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = new Chart(chartRef.current.getContext('2d'), { ...config, type: chartType });
    return () => chart.destroy();
  }, [config, config.type, chartType]);

  if (!data.length) return (
    <div className="card shadow-sm mb-4 text-center p-5 text-muted">
      <i className="bi bi-bar-chart-line display-4 mb-3"></i>
      ไม่มีข้อมูลกราฟ
    </div>
  );

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0"></h5>
        <div className="d-flex gap-4 text-nowrap">
          <span className="text-muted">ประเภท:</span>
          <select value={chartType} onChange={e => setChartType(e.target.value)} className="form-select form-select-sm">
            <option value="bar">Bar</option>
            <option value="line">Line</option>
          </select>
          <span className="text-muted">เรียงตาม:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="form-select form-select-sm">
            <option value="TotalQty">จำนวนขาย</option>
            <option value="total_customer">ลูกค้า</option>
            <option value="repeat_rate">ซื้อซ้ำ (%)</option>
            <option value="Item_Total">ยอดขาย</option>
          </select>
          <span className="text-muted">แสดง:</span>
          <select value={topCount} onChange={e => setTopCount(+e.target.value)} className="form-select form-select-sm">
            {[5,10,15,20].map(n => <option key={n} value={n}>Top {n}</option>)}
          </select>
        </div>
      </div>
      <div className="card-body" style={{ height: 400 }}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );
};

export default TopSellingChart;